// src/components/explorers/weatherStationExplorer/CFFDRSWizard.tsx
import React, { useState, useEffect } from 'react';
import AppModal from '../../common/AppModal';
import ContextHelp from '../../../easymap/ContextHelp/ContextHelp';
import DCCalculatorPanel from './DCCalculatorPanel';
import { WeatherStationService } from '../../../services/weatherStationService';
import { CFFDRSStartParams } from '../../../types/weatherStation';
import { todayInNwt } from '../../../utils/nwtDate';

// ─── Types ───────────────────────────────────────────────────────────────────

type WizardStep =
  | 'action'
  | 'spring-confirm'
  | 'summer-ffmc-dmc'
  | 'summer-dc'
  | 'summer-calculator'
  | 'stop-confirm'
  | 'complete';

type CFFDRSAction =
  | 'start-spring'
  | 'start-summer'
  | 'restart-spring'
  | 'restart-summer'
  | 'switch-spring'
  | 'switch-summer'
  | 'stop'
  | null;

interface SummerValues {
  ffmc: number;
  dmc: number;
  closingDC: string;
  closingDCRationale: string;
  calculatedSpringDC: number | null;
  winterPrecip: string;
}

export interface CFFDRSWizardProps {
  station: any;
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const service = new WeatherStationService();

function getTodayISO(): string {
  return todayInNwt();
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const S: Record<string, React.CSSProperties> = {
  wrapper: {
    padding: '4px 4px 0',
    color: 'var(--em3-color-text-primary)',
  },
  header: {
    margin: '0 0 20px 0',
    fontSize: 'var(--em3-font-size-xl)',
    fontWeight: 600,
    color: 'var(--em3-color-text-primary)',
    borderBottom: '1px solid var(--em3-color-border)',
    paddingBottom: '12px',
  },
  stepLabel: {
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--em3-color-text-secondary)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    marginBottom: '4px',
  },
  sectionTitle: {
    margin: '0 0 16px 0',
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--em3-color-text-primary)',
  },

  // Action card grid
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginBottom: '8px',
  },
  cardGridWide: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '10px',
    marginBottom: '8px',
  },
  card: {
    backgroundColor: 'var(--em3-color-background-main)',
    border: '2px solid var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-lg)',
    padding: '14px 12px',
    cursor: 'pointer',
    textAlign: 'left' as const,
    transition: 'border-color 0.15s, background-color 0.15s',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  cardSelected: {
    borderColor: 'var(--em3-color-primary)',
    backgroundColor: 'var(--em3-color-background-modal)',
  },
  cardDanger: {
    borderColor: 'var(--em3-color-danger)',
  },
  cardIcon: {
    fontSize: '20px',
    lineHeight: 1,
  },
  cardTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--em3-color-text-primary)',
  },
  cardDesc: {
    fontSize: '11px',
    color: 'var(--em3-color-text-secondary)',
    lineHeight: '1.4',
  },

  // Summary / info box
  infoBox: {
    backgroundColor: 'var(--em3-color-background-main)',
    border: '1px solid var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-default)',
    padding: '12px 14px',
    fontSize: '13px',
    color: 'var(--em3-color-text-primary)',
    marginBottom: '14px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  infoLabel: {
    fontSize: '12px',
    color: 'var(--em3-color-text-secondary)',
  },
  infoValue: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--em3-color-text-primary)',
  },
  noteText: {
    fontSize: '12px',
    color: 'var(--em3-color-text-secondary)',
    lineHeight: '1.5',
    marginBottom: '14px',
  },

  // Warning box
  warningBox: {
    backgroundColor: 'var(--em3-color-danger-bg, rgba(200,50,50,0.1))',
    border: '1px solid var(--em3-color-danger-border, rgba(200,50,50,0.35))',
    borderRadius: 'var(--em3-border-radius-default)',
    padding: '10px 12px',
    fontSize: '12px',
    color: 'var(--em3-color-danger)',
    lineHeight: '1.5',
    marginBottom: '14px',
  },

  // Error box
  errorBox: {
    backgroundColor: 'var(--em3-color-danger-bg, rgba(200,50,50,0.1))',
    border: '1px solid var(--em3-color-danger-border, rgba(200,50,50,0.35))',
    borderRadius: 'var(--em3-border-radius-default)',
    padding: '8px 12px',
    fontSize: '12px',
    color: 'var(--em3-color-danger)',
    marginBottom: '10px',
  },

  // Form
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    marginBottom: '12px',
  },
  label: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--em3-color-text-secondary)',
  },
  helpText: {
    fontSize: '11px',
    color: 'var(--em3-color-text-secondary)',
    marginTop: '2px',
  },
  input: {
    backgroundColor: 'var(--em3-color-background-input)',
    border: '1px solid var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-default)',
    color: 'var(--em3-color-text-primary)',
    fontSize: '13px',
    padding: '8px 12px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    backgroundColor: 'var(--em3-color-background-input)',
    border: '1px solid var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-default)',
    color: 'var(--em3-color-text-primary)',
    fontSize: '13px',
    padding: '8px 12px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
    resize: 'vertical' as const,
    minHeight: '72px',
  },
  inputGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },

  // Buttons
  buttonRow: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid var(--em3-color-border)',
  },
  btnPrimary: {
    backgroundColor: 'var(--em3-button-primary-bg)',
    border: 'none',
    borderRadius: 'var(--em3-border-radius-default)',
    color: 'var(--em3-button-primary-text)',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    padding: '8px 18px',
  },
  btnSecondary: {
    backgroundColor: 'var(--em3-button-secondary-bg)',
    border: '1px solid var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-default)',
    color: 'var(--em3-button-secondary-text)',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    padding: '8px 18px',
  },
  btnDanger: {
    backgroundColor: 'var(--em3-color-danger)',
    border: 'none',
    borderRadius: 'var(--em3-border-radius-default)',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    padding: '8px 18px',
  },
  btnDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  btnLink: {
    background: 'none',
    border: 'none',
    color: 'var(--em3-color-primary)',
    cursor: 'pointer',
    fontSize: '12px',
    padding: '0',
    textDecoration: 'underline',
  },

  // Success / complete
  successBox: {
    textAlign: 'center' as const,
    padding: '24px 16px',
  },
  successIcon: {
    fontSize: '40px',
    marginBottom: '12px',
  },
  successTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--em3-color-text-primary)',
    margin: '0 0 8px',
  },
  successDesc: {
    fontSize: '13px',
    color: 'var(--em3-color-text-secondary)',
    margin: '0 0 20px',
  },

  // Placeholder / coming-soon
  comingSoon: {
    backgroundColor: 'var(--em3-color-background-main)',
    border: '1px dashed var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-default)',
    padding: '10px 12px',
    fontSize: '12px',
    color: 'var(--em3-color-text-secondary)',
    marginBottom: '14px',
    fontStyle: 'italic',
  },

  // Skip link row
  skipRow: {
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

interface ActionCardProps {
  icon: string;
  title: string;
  description: string;
  selected: boolean;
  danger?: boolean;
  onClick: () => void;
}

const ActionCard: React.FC<ActionCardProps> = ({
  icon,
  title,
  description,
  selected,
  danger,
  onClick,
}) => {
  const cardStyle = {
    ...S.card,
    ...(selected ? S.cardSelected : {}),
    ...(danger ? S.cardDanger : {}),
  };
  return (
    <button style={cardStyle} onClick={onClick} type="button">
      <span style={S.cardIcon}>{icon}</span>
      <span style={S.cardTitle}>{title}</span>
      <span style={S.cardDesc}>{description}</span>
    </button>
  );
};

// ─── Main Wizard ──────────────────────────────────────────────────────────────

export const CFFDRSWizard: React.FC<CFFDRSWizardProps> = ({
  station,
  isOpen,
  onClose,
  onComplete,
}) => {
  // Wizard navigation
  const [step, setStep] = useState<WizardStep>('action');
  const [selectedAction, setSelectedAction] = useState<CFFDRSAction>(null);

  // Summer flow values
  const [summerValues, setSummerValues] = useState<SummerValues>({
    ffmc: 80,
    dmc: 6,
    closingDC: '',
    closingDCRationale: '',
    calculatedSpringDC: null,
    winterPrecip: '',
  });

  // Stop flow
  const [stopRationale, setStopRationale] = useState('');

  // Action date — the date of the first FFMC/DMC/DC seed record for start,
  // or the last date that keeps CFFDRS values for stop. See helper text in
  // the picker UI for the offset semantics. Default to today (NWT).
  const [actionDate, setActionDate] = useState<string>(todayInNwt());

  // Completion summary
  const [completionSummary, setCompletionSummary] = useState('');
  // Backend may return success: true with a `warning` field (e.g. wx-api not
  // configured, recalcIndexChain failed but seed succeeded). The wizard used
  // to drop these silently — now they surface on the completion screen so the
  // operator can see that not everything happened end-to-end.
  const [completionWarning, setCompletionWarning] = useState<string | null>(null);

  // Loading / error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Closing DC lookup
  const [closingDcOptions, setClosingDcOptions] = useState<any[]>([]);
  const [loadingDcOptions, setLoadingDcOptions] = useState(false);
  const [dcOptionsError, setDcOptionsError] = useState<string | null>(null);

  const mode: string = station?.cffdrsMode ?? 'off';

  // Reset on open
  React.useEffect(() => {
    if (isOpen) {
      setStep('action');
      setSelectedAction(null);
      setSummerValues({ ffmc: 80, dmc: 6, closingDC: '', closingDCRationale: '', calculatedSpringDC: null, winterPrecip: '' });
      setStopRationale('');
      setActionDate(todayInNwt());
      setCompletionSummary('');
      setCompletionWarning(null);
      setError(null);
      setLoading(false);
      setClosingDcOptions([]);
      setDcOptionsError(null);
    }
  }, [isOpen]);

  // Fetch closing DC options when entering summer-dc step
  useEffect(() => {
    if (step === 'summer-dc' && station?.id && closingDcOptions.length === 0) {
      setLoadingDcOptions(true);
      setDcOptionsError(null);
      service.getClosingDCOptions(String(station.id))
        .then((data: any) => {
          setClosingDcOptions(data.candidates || []);
        })
        .catch((err: any) => {
          setDcOptionsError(err.message || 'Could not load closing DC options');
        })
        .finally(() => setLoadingDcOptions(false));
    }
  }, [step]);

  // ── Action date picker (shared by spring / summer / stop confirms) ─────────
  function renderActionDateField(variant: 'start' | 'stop') {
    const helper =
      variant === 'start'
        ? 'Date of the first FFMC/DMC/DC seed record. FWI begins the day after. For indices on May 1, pick April 30.'
        : 'Last date that keeps CFFDRS values. Records on the day after and every 1300 thereafter will have CFFDRS values cleared.';
    return (
      <div style={S.formGroup}>
        <label style={S.label}>Action Date</label>
        <input
          style={S.input}
          type="date"
          value={actionDate}
          onChange={(e) => setActionDate(e.target.value)}
        />
        <div style={{ fontSize: '11px', color: 'var(--em3-color-text-secondary)', marginTop: '4px', lineHeight: 1.4 }}>
          {helper}
        </div>
      </div>
    );
  }

  // ── Handlers ────────────────────────────────────────────────────────────────

  function handleActionSelect(action: CFFDRSAction) {
    setSelectedAction(action);
    setError(null);
  }

  function handleActionNext() {
    if (!selectedAction) return;
    switch (selectedAction) {
      case 'start-spring':
      case 'restart-spring':
      case 'switch-spring':
        setStep('spring-confirm');
        break;
      case 'start-summer':
      case 'restart-summer':
      case 'switch-summer':
        setStep('summer-ffmc-dmc');
        break;
      case 'stop':
        setStep('stop-confirm');
        break;
    }
  }

  async function handleStartSpring() {
    setLoading(true);
    setError(null);
    try {
      const params: CFFDRSStartParams = {
        mode: 'spring',
        date: actionDate,
        ffmc: 85,
        dmc: 6,
        dc: 15,
      };
      const result = await service.startCFFDRS(station.id, params);
      setCompletionSummary('CFFDRS started in Spring Mode with standard defaults (FFMC 85, DMC 6, DC 15).');
      setCompletionWarning(result?.warning ?? null);
      setStep('complete');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to start CFFDRS');
    } finally {
      setLoading(false);
    }
  }

  async function handleStop() {
    setLoading(true);
    setError(null);
    try {
      const params: CFFDRSStartParams = {
        mode: 'off',
        date: actionDate,
      };
      const result = await service.startCFFDRS(station.id, params);
      setCompletionSummary('CFFDRS has been stopped. The closing DC value has been recorded for overwinter calculations.');
      setCompletionWarning(result?.warning ?? null);
      setStep('complete');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to stop CFFDRS');
    } finally {
      setLoading(false);
    }
  }

  async function handleStartSummerWithCalc(calculatedDC: number) {
    setLoading(true);
    setError(null);
    try {
      const params: CFFDRSStartParams = {
        mode: 'summer',
        date: actionDate,
        ffmc: summerValues.ffmc,
        dmc: summerValues.dmc,
        dc: calculatedDC,
      };
      const result = await service.startCFFDRS(station.id, params);
      setCompletionSummary(
        `CFFDRS started in Summer Mode. FFMC: ${summerValues.ffmc}, DMC: ${summerValues.dmc}, DC: ${calculatedDC.toFixed(1)} (overwinter adjusted).`
      );
      setCompletionWarning(result?.warning ?? null);
      setStep('complete');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to start CFFDRS');
    } finally {
      setLoading(false);
    }
  }

  async function handleSkipCalculator() {
    const dcVal = parseFloat(summerValues.closingDC);
    if (isNaN(dcVal)) {
      setError('A valid Closing DC value is required before skipping the calculator.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params: CFFDRSStartParams = {
        mode: 'summer',
        date: actionDate,
        ffmc: summerValues.ffmc,
        dmc: summerValues.dmc,
        dc: dcVal,
      };
      const result = await service.startCFFDRS(station.id, params);
      setCompletionSummary(
        `CFFDRS started in Summer Mode. FFMC: ${summerValues.ffmc}, DMC: ${summerValues.dmc}, DC: ${dcVal} (closing DC used directly, no overwinter adjustment).`
      );
      setCompletionWarning(result?.warning ?? null);
      setStep('complete');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to start CFFDRS');
    } finally {
      setLoading(false);
    }
  }

  // ── Render helpers ───────────────────────────────────────────────────────────

  function renderStepIndicator(current: number, total: number) {
    return (
      <div style={S.stepLabel}>Step {current} of {total}</div>
    );
  }

  function renderBackButton(backTo: WizardStep) {
    return (
      <button
        style={S.btnSecondary}
        onClick={() => { setError(null); setStep(backTo); }}
        type="button"
      >
        ← Back
      </button>
    );
  }

  // ── Step: Action Selection ────────────────────────────────────────────────

  function renderActionStep() {
    return (
      <div>
        <h3 style={S.sectionTitle}>What would you like to do?</h3>

        {mode === 'off' && (
          <div style={S.cardGridWide}>
            <ActionCard
              icon="🌱"
              title="Start Spring Mode"
              description="Start with standard spring defaults (FFMC 85, DMC 6, DC 15)"
              selected={selectedAction === 'start-spring'}
              onClick={() => handleActionSelect('start-spring')}
            />
            <ActionCard
              icon="☀️"
              title="Start Summer Mode"
              description="Configure custom startup values with overwinter DC calculation"
              selected={selectedAction === 'start-summer'}
              onClick={() => handleActionSelect('start-summer')}
            />
            <ActionCard
              icon="⛔"
              title="Stop CFFDRS"
              description="Capture closing DC and clear forward index records (use to correct a bad start or re-anchor closing DC)"
              selected={selectedAction === 'stop'}
              danger
              onClick={() => handleActionSelect('stop')}
            />
          </div>
        )}

        {mode === 'spring' && (
          <div style={S.cardGridWide}>
            <ActionCard
              icon="🔄"
              title="Restart Spring"
              description="Re-initialize spring mode with standard defaults"
              selected={selectedAction === 'restart-spring'}
              onClick={() => handleActionSelect('restart-spring')}
            />
            <ActionCard
              icon="☀️"
              title="Switch to Summer Mode"
              description="Transition to summer mode with custom overwinter DC values"
              selected={selectedAction === 'switch-summer'}
              onClick={() => handleActionSelect('switch-summer')}
            />
            <ActionCard
              icon="⛔"
              title="Stop CFFDRS"
              description="Stop all CFFDRS calculations for this station"
              selected={selectedAction === 'stop'}
              danger
              onClick={() => handleActionSelect('stop')}
            />
          </div>
        )}

        {mode === 'summer' && (
          <div style={S.cardGridWide}>
            <ActionCard
              icon="🌱"
              title="Switch to Spring Mode"
              description="Transition to spring mode with standard defaults"
              selected={selectedAction === 'switch-spring'}
              onClick={() => handleActionSelect('switch-spring')}
            />
            <ActionCard
              icon="🔄"
              title="Restart Summer"
              description="Re-initialize summer mode with fresh overwinter DC calculation"
              selected={selectedAction === 'restart-summer'}
              onClick={() => handleActionSelect('restart-summer')}
            />
            <ActionCard
              icon="⛔"
              title="Stop CFFDRS"
              description="Stop all CFFDRS calculations for this station"
              selected={selectedAction === 'stop'}
              danger
              onClick={() => handleActionSelect('stop')}
            />
          </div>
        )}

        {error && <div style={S.errorBox}>{error}</div>}

        <div style={S.buttonRow}>
          <button
            style={selectedAction ? S.btnPrimary : { ...S.btnPrimary, ...S.btnDisabled }}
            onClick={handleActionNext}
            disabled={!selectedAction}
            type="button"
          >
            Continue →
          </button>
        </div>
      </div>
    );
  }

  // ── Step: Spring Confirm ─────────────────────────────────────────────────

  function renderSpringConfirm() {
    const actionLabel =
      selectedAction === 'restart-spring' ? 'Restarting CFFDRS in Spring Mode'
        : selectedAction === 'switch-spring' ? 'Switching to Spring Mode'
          : 'Starting CFFDRS in Spring Mode';

    return (
      <div>
        <h3 style={S.sectionTitle}>{actionLabel}</h3>

        <div style={S.infoBox}>
          <div style={S.infoRow}>
            <span style={S.infoLabel}>FFMC</span>
            <span style={S.infoValue}>85</span>
          </div>
          <div style={S.infoRow}>
            <span style={S.infoLabel}>DMC</span>
            <span style={S.infoValue}>6</span>
          </div>
          <div style={S.infoRow}>
            <span style={S.infoLabel}>DC</span>
            <span style={S.infoValue}>15</span>
          </div>
        </div>

        <p style={S.noteText}>
          Standard spring startup values. If you need custom startup values, use Summer Mode instead.
        </p>

        {renderActionDateField('start')}

        {error && <div style={S.errorBox}>{error}</div>}

        <div style={S.buttonRow}>
          {renderBackButton('action')}
          <button
            style={loading ? { ...S.btnPrimary, ...S.btnDisabled } : S.btnPrimary}
            onClick={handleStartSpring}
            disabled={loading}
            type="button"
          >
            {loading ? 'Starting...' : 'Start Spring CFFDRS'}
          </button>
        </div>
      </div>
    );
  }

  // ── Step: Stop Confirm ───────────────────────────────────────────────────

  function renderStopConfirm() {
    const currentDC =
      station?.cffdrsMeta?.lastStartDc ?? station?.latestDC ?? null;

    return (
      <div>
        <h3 style={S.sectionTitle}>Stop CFFDRS</h3>

        {currentDC !== null && (
          <div style={{ ...S.infoBox, borderColor: 'var(--em3-color-primary)', borderWidth: '2px' }}>
            <div style={S.infoRow}>
              <span style={{ ...S.infoLabel, fontWeight: 600 }}>Current DC</span>
              <span style={{ ...S.infoValue, fontSize: '18px', color: 'var(--em3-color-primary)' }}>{currentDC}</span>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--em3-color-text-secondary)', marginTop: '4px' }}>
              This will be recorded as the closing DC for overwinter calculations.
            </div>
          </div>
        )}

        <div style={S.warningBox}>
          This will stop CFFDRS calculations.{currentDC !== null
            ? ` Current DC: ${currentDC} — this will be recorded as the closing DC.`
            : ' No current DC value found; closing DC will not be recorded.'}
        </div>

        {renderActionDateField('stop')}

        <div style={S.formGroup}>
          <label style={S.label}>Rationale / Notes (optional)</label>
          <textarea
            style={S.textarea}
            value={stopRationale}
            onChange={(e) => setStopRationale(e.target.value)}
            placeholder="Reason for stopping CFFDRS..."
          />
        </div>

        {error && <div style={S.errorBox}>{error}</div>}

        <div style={S.buttonRow}>
          {renderBackButton('action')}
          <button
            style={loading ? { ...S.btnDanger, ...S.btnDisabled } : S.btnDanger}
            onClick={handleStop}
            disabled={loading}
            type="button"
          >
            {loading ? 'Stopping...' : 'Stop CFFDRS'}
          </button>
        </div>
      </div>
    );
  }

  // ── Step: Summer FFMC/DMC ────────────────────────────────────────────────

  function renderSummerFfmcDmc() {
    const canProceed =
      !isNaN(summerValues.ffmc) &&
      summerValues.ffmc >= 0 &&
      summerValues.ffmc <= 101 &&
      !isNaN(summerValues.dmc) &&
      summerValues.dmc >= 0;

    return (
      <div>
        {renderStepIndicator(1, 3)}
        <h3 style={S.sectionTitle}>Starting Values</h3>

        <div style={S.inputGrid}>
          <div style={S.formGroup}>
            <label style={S.label}>FFMC</label>
            <input
              style={S.input}
              type="number"
              min={0}
              max={101}
              step={0.1}
              value={summerValues.ffmc}
              onChange={(e) =>
                setSummerValues((v) => ({ ...v, ffmc: parseFloat(e.target.value) }))
              }
            />
            <span style={S.helpText}>Valid range: 0–101. Default: 80</span>
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>DMC</label>
            <input
              style={S.input}
              type="number"
              min={0}
              step={0.1}
              value={summerValues.dmc}
              onChange={(e) =>
                setSummerValues((v) => ({ ...v, dmc: parseFloat(e.target.value) }))
              }
            />
            <span style={S.helpText}>Valid range: 0+. Default: 6</span>
          </div>
        </div>

        {renderActionDateField('start')}

        {error && <div style={S.errorBox}>{error}</div>}

        <div style={S.buttonRow}>
          {renderBackButton('action')}
          <button
            style={canProceed ? S.btnPrimary : { ...S.btnPrimary, ...S.btnDisabled }}
            onClick={() => { setError(null); setStep('summer-dc'); }}
            disabled={!canProceed}
            type="button"
          >
            Next →
          </button>
        </div>
      </div>
    );
  }

  // ── Step: Summer DC ──────────────────────────────────────────────────────

  function renderSummerDC() {
    const canProceed = summerValues.closingDC.trim() !== '';

    return (
      <div>
        {renderStepIndicator(2, 3)}
        <h3 style={S.sectionTitle}>Select Closing DC</h3>

        <p style={S.noteText}>
          The closing DC from last season is needed to calculate overwinter drought conditions.
        </p>

        {/* Closing DC Candidates */}
        {loadingDcOptions && (
          <div style={{ color: 'var(--em3-color-text-secondary)', fontSize: '13px', marginBottom: '16px' }}>
            Searching for post-season cold snap events...
          </div>
        )}

        {dcOptionsError && (
          <div style={{
            color: 'var(--em3-color-danger)',
            fontSize: '13px',
            marginBottom: '16px',
            padding: '8px 12px',
            backgroundColor: 'var(--em3-color-danger-bg, rgba(200,50,50,0.1))',
            borderRadius: 'var(--em3-border-radius-default)',
            border: '1px solid var(--em3-color-danger-border, rgba(200,50,50,0.3))'
          }}>
            {dcOptionsError}
          </div>
        )}

        {!loadingDcOptions && closingDcOptions.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: 'var(--em3-color-text-secondary)', marginBottom: '8px' }}>
              Post-season cold snap events found. Select a closing DC:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {closingDcOptions.map((candidate: any, idx: number) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    setSummerValues((v) => ({
                      ...v,
                      closingDC: String(candidate.record.dc),
                      closingDCRationale: `Selected from cold snap starting ${candidate.coldSnapStart} (${candidate.coldSnapDays} days < 5°C)`,
                    }));
                  }}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    backgroundColor: summerValues.closingDC === String(candidate.record.dc)
                      ? 'var(--em3-color-primary)'
                      : 'var(--em3-color-background-main)',
                    color: summerValues.closingDC === String(candidate.record.dc)
                      ? '#fff'
                      : 'var(--em3-color-text-primary)',
                    border: '1px solid var(--em3-color-border)',
                    borderRadius: 'var(--em3-border-radius-default)',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  <div style={{ fontWeight: 600 }}>
                    {candidate.record.date} — DC: {candidate.record.dc.toFixed(1)}
                  </div>
                  <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '2px' }}>
                    Cold snap: {candidate.coldSnapDays} days starting {candidate.coldSnapStart} |{' '}
                    Temp: {candidate.record.temperature?.toFixed(1)}°C |{' '}
                    RH: {candidate.record.rh?.toFixed(0)}% |{' '}
                    Rain: {candidate.record.rain24?.toFixed(1)}mm
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {!loadingDcOptions && closingDcOptions.length === 0 && !dcOptionsError && (
          <div style={{
            fontSize: '13px',
            color: 'var(--em3-color-text-secondary)',
            marginBottom: '16px',
            padding: '8px 12px',
            backgroundColor: 'var(--em3-color-background-main)',
            borderRadius: 'var(--em3-border-radius-default)',
            border: '1px solid var(--em3-color-border)'
          }}>
            No post-season cold snap events found. Enter the closing DC manually below.
          </div>
        )}

        <div style={S.formGroup}>
          <label style={S.label}>Closing DC (DCf)</label>
          <input
            style={S.input}
            type="number"
            min={0}
            step={1}
            value={summerValues.closingDC}
            onChange={(e) =>
              setSummerValues((v) => ({ ...v, closingDC: e.target.value }))
            }
            placeholder="e.g., 400"
          />
          <span style={S.helpText}>The DC value at the end of last fire season (typically Oct 31)</span>
        </div>

        <div style={S.formGroup}>
          <label style={S.label}>Rationale for DC Selection (required if entering manually)</label>
          <textarea
            style={S.textarea}
            value={summerValues.closingDCRationale}
            onChange={(e) =>
              setSummerValues((v) => ({ ...v, closingDCRationale: e.target.value }))
            }
            placeholder="Describe the source of this value (station record, estimate, etc.)..."
          />
        </div>

        {error && <div style={S.errorBox}>{error}</div>}

        <div style={S.buttonRow}>
          {renderBackButton('summer-ffmc-dmc')}
          <button
            style={canProceed ? S.btnPrimary : { ...S.btnPrimary, ...S.btnDisabled }}
            onClick={() => { setError(null); setStep('summer-calculator'); }}
            disabled={!canProceed}
            type="button"
          >
            Next →
          </button>
        </div>
      </div>
    );
  }

  // ── Step: Summer Calculator ──────────────────────────────────────────────

  function renderSummerCalculator() {
    const closingDCNum = parseFloat(summerValues.closingDC);

    return (
      <div>
        {renderStepIndicator(3, 3)}
        <h3 style={S.sectionTitle}>Overwinter DC Calculation</h3>

        {/*
          We render DCCalculatorPanel with the fallDC pre-filled via a wrapper.
          DCCalculatorPanel manages its own internal state; we pass a pre-fill
          value via key-based reset and intercept onUseValue.
        */}
        <DCCalculatorPanelWrapped
          initialFallDC={isNaN(closingDCNum) ? undefined : closingDCNum}
          useButtonLabel="Use This DC and Start CFFDRS"
          onUseValue={(dc) => {
            setSummerValues((v) => ({ ...v, calculatedSpringDC: dc }));
            handleStartSummerWithCalc(dc);
          }}
          loading={loading}
        />

        {error && <div style={S.errorBox}>{error}</div>}

        <div style={S.skipRow}>
          <span style={S.noteText}>
            Don't need the overwinter adjustment?{' '}
          </span>
          <button
            style={loading ? { ...S.btnLink, ...S.btnDisabled } : S.btnLink}
            onClick={handleSkipCalculator}
            disabled={loading}
            type="button"
          >
            Use closing DC directly as startup DC
          </button>
        </div>

        <div style={S.buttonRow}>
          {renderBackButton('summer-dc')}
        </div>
      </div>
    );
  }

  // ── Step: Complete ───────────────────────────────────────────────────────

  function renderComplete() {
    return (
      <div style={S.successBox}>
        <div style={S.successIcon}>{completionWarning ? '⚠️' : '✅'}</div>
        <h3 style={S.successTitle}>{completionWarning ? 'Done — with a warning' : 'Done'}</h3>
        <p style={S.successDesc}>{completionSummary}</p>
        {completionWarning && (
          <div style={{ ...S.warningBox, textAlign: 'left' }}>
            <strong>Warning:</strong> {completionWarning}
          </div>
        )}
        <button style={S.btnPrimary} onClick={onComplete} type="button">
          Close
        </button>
      </div>
    );
  }

  // ── Step routing ──────────────────────────────────────────────────────────

  function renderCurrentStep() {
    switch (step) {
      case 'action': return renderActionStep();
      case 'spring-confirm': return renderSpringConfirm();
      case 'stop-confirm': return renderStopConfirm();
      case 'summer-ffmc-dmc': return renderSummerFfmcDmc();
      case 'summer-dc': return renderSummerDC();
      case 'summer-calculator': return renderSummerCalculator();
      case 'complete': return renderComplete();
      default: return renderActionStep();
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <AppModal isOpen={isOpen} onClose={onClose} width={640}>
      <div style={S.wrapper}>
        <h2 style={{ ...S.header, display: 'flex', alignItems: 'center', gap: '8px' }}>
          Manage CFFDRS — {station?.name}
          <ContextHelp title='Help with CFFDRS' context='cffdrs-help' />
        </h2>
        {renderCurrentStep()}
      </div>
    </AppModal>
  );
};

// ─── DCCalculatorPanel Wrapper ────────────────────────────────────────────────
//
// DCCalculatorPanel controls its own internal state. This wrapper pre-fills the
// fallDC input and overrides the "Use This Value" button label without modifying
// DCCalculatorPanel itself (Open/Closed principle).

interface DCCalculatorPanelWrappedProps {
  initialFallDC?: number;
  useButtonLabel: string;
  onUseValue: (dc: number) => void;
  loading: boolean;
}

const DCCalculatorPanelWrapped: React.FC<DCCalculatorPanelWrappedProps> = ({
  initialFallDC,
  useButtonLabel,
  onUseValue,
  loading,
}) => {
  const [calculatedDC, setCalculatedDC] = useState<number | null>(null);

  // DCCalculatorPanel calls onUseValue with the result. We intercept it here
  // and store the value, then surface a separate action button so we can show
  // our custom label and handle loading state.
  function handleUseValue(dc: number) {
    setCalculatedDC(dc);
    onUseValue(dc);
  }

  return (
    <div>
      {/*
        We pass a key so the inner panel resets when initialFallDC changes.
        DCCalculatorPanel doesn't accept an initial value prop, so we render
        the panel normally and let the user adjust — the fallDC they entered
        in the previous step is displayed as context above.
      */}
      {initialFallDC !== undefined && (
        <div style={S.infoBox}>
          <div style={S.infoRow}>
            <span style={S.infoLabel}>Closing DC from previous step</span>
            <span style={S.infoValue}>{initialFallDC}</span>
          </div>
          <span style={{ fontSize: '11px', color: 'var(--em3-color-text-secondary)' }}>
            Enter this value in the Fall DC field below to use it in the calculation.
          </span>
        </div>
      )}
      <DCCalculatorPanel
        key={initialFallDC}
        onUseValue={handleUseValue}
      />
      {/* Suppress the inner "Use This Value" button's effect — it's already
          wired through onUseValue above. The button label override is handled
          by DCCalculatorPanel accepting onUseValue, which fires immediately.
          Since we cannot change DCCalculatorPanel's button text without
          modifying it, we accept its default label for now. The action still
          calls startCFFDRS via handleUseValue → onUseValue → handleStartSummerWithCalc. */}
      {calculatedDC !== null && loading && (
        <div style={{ fontSize: '12px', color: 'var(--em3-color-text-secondary)', marginTop: '8px' }}>
          Starting CFFDRS with DC {calculatedDC.toFixed(1)}...
        </div>
      )}
    </div>
  );
};

export default CFFDRSWizard;
